import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { HardHat, Lock, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const { token } = useParams();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking | form | invalid
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/auth/reset-password/${token}`)
      .then((r) => setState(r.data.valid ? "form" : "invalid"))
      .catch(() => setState("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setErr("Passwords don't match."); return; }
    setBusy(true);
    try {
      const r = await api.post("/auth/reset-password", { token, password });
      setUser(r.data.user);
      navigate("/dashboard", { replace: true });
    } catch (e2) { setErr(e2?.response?.data?.detail || "Could not reset password."); setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-emerald-950 flex items-center justify-center p-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-6 text-white">
          <div className="h-9 w-9 rounded-lg bg-lime-400/20 flex items-center justify-center"><HardHat className="h-5 w-5 text-lime-300" strokeWidth={2.2} /></div>
          <span className="font-heading font-bold text-lg tracking-tight">BidMatchAI</span>
        </Link>
        <div className="bg-white rounded-3xl shadow-2xl p-8">
          {state === "checking" && <div className="flex justify-center py-8"><div className="h-8 w-8 border-[3px] border-slate-200 border-t-emerald-700 rounded-full animate-spin" /></div>}
          {state === "invalid" && (
            <div className="text-center py-4" data-testid="reset-invalid">
              <h1 className="font-heading text-2xl font-bold text-slate-900">Link expired</h1>
              <p className="text-slate-500 mt-2 text-sm">This reset link is invalid or has expired.</p>
              <Link to="/forgot-password" className="inline-block mt-6 text-sm text-emerald-700 hover:text-emerald-800 font-medium">Request a new link</Link>
            </div>
          )}
          {state === "form" && (
            <>
              <h1 className="font-heading text-2xl font-bold text-slate-900">Set a new password</h1>
              <p className="text-slate-500 mt-1 text-sm">Choose a strong password (at least 8 characters).</p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password" type="password" required placeholder="New password"
                    className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30" />
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm" type="password" required placeholder="Confirm new password"
                    className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30" />
                </div>
                {err && <p className="text-sm text-red-600" data-testid="reset-error">{err}</p>}
                <button type="submit" disabled={busy} data-testid="reset-submit"
                  className="w-full bg-emerald-900 text-white rounded-xl py-3.5 text-sm font-semibold hover:bg-emerald-800 disabled:opacity-60 transition-colors">
                  {busy ? "Saving…" : "Reset password & sign in"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
